import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { DealType } from '../../../../../enums/DealType';
import { PortfolioFilterType } from '../../../../../enums/PortfolioFilterType';
import { useDealType } from '../../../../../hooks';
import { usePortfolioFilter } from '../../../../../hooks/UsePortfolioFilter';
import { AppState } from '../../../../../store/AppState';
import { setOpenFilterCount } from '../../../../../store/portfolio/PortfolioActions';
import { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import { FilterItemResource } from '../../../../../types/FilterTypes';
import MultiSelectDropdown from '../../../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import { Badge } from '../../../../style';

const PortfolioDealTypeFilter: FC = () => {
  const { dealType, openFilterCount } = useSelector<AppState, PortfolioState>(
    (state) => state.portfolio,
  );
  const { setSelectedFilter, getPortfolioView } = usePortfolioFilter();
  const { getDealTypeLabel, getDealTypeNumber, getColorCode } = useDealType();
  const [isLeaseSelected, setIsLeaseSelected] = useState<boolean>(false);
  const [isFinanceSelected, setIsFinanceSelected] = useState<boolean>(false);
  const [isCashSelected, setIsCashSelected] = useState<boolean>(false);

  const currentView = getPortfolioView(dealType);
  const dispatch = useDispatch();

  const onOpen = () => {
    const newOpenFilterCount = openFilterCount + 1;
    dispatch(setOpenFilterCount(newOpenFilterCount));
  };
  const onClose = () => {
    const newOpenFilterCount = openFilterCount === 0 ? 0 : openFilterCount - 1;
    dispatch(setOpenFilterCount(newOpenFilterCount));
  };

  const enableDealTypeLabels = (dealTypes: FilterItemResource[]) => {
    dealTypes?.forEach((dealType) => {
      switch (parseInt(dealType.key)) {
        case getDealTypeNumber(DealType.Lease):
          setIsLeaseSelected(true);
          break;
        case getDealTypeNumber(DealType.Finance):
          setIsFinanceSelected(true);
          break;
        case getDealTypeNumber(DealType.Cash):
          setIsCashSelected(true);
          break;
        default:
          break;
      }
    });
  };

  useEffect(() => {
    setIsLeaseSelected(false);
    setIsFinanceSelected(false);
    setIsCashSelected(false);
    if (currentView?.selectedFilters.dealTypes?.length === 0) {
      enableDealTypeLabels(currentView?.filters.dealTypes);
    } else {
      enableDealTypeLabels(currentView?.selectedFilters.dealTypes);
    }
  }, [currentView?.filters.dealTypes, currentView?.selectedFilters.dealTypes]);

  const getSubLabel = (): JSX.Element => {
    return (
      <>
        {isLeaseSelected && (
          <DealTypeFilterBadge backgroundColor={getColorCode(DealType.Lease)}>
            L
          </DealTypeFilterBadge>
        )}
        {isFinanceSelected && (
          <DealTypeFilterBadge backgroundColor={getColorCode(DealType.Finance)}>
            F
          </DealTypeFilterBadge>
        )}
        {isCashSelected && (
          <DealTypeFilterBadge backgroundColor={getColorCode(DealType.Cash)}>C</DealTypeFilterBadge>
        )}
      </>
    );
  };

  return (
    <Wrapper data-testid='deal-type-filter-dropdown'>
      <MultiSelectDropdown
        placeholder={'Deal Type'}
        displaySelectedLabels={false}
        options={currentView?.filters.dealTypes.map((dealType) => {
          return { ...dealType, label: getDealTypeLabel(parseInt(dealType.key)) };
        })}
        onChange={(values) => {
          setSelectedFilter(
            PortfolioFilterType.DealType,
            values.map((dealType) => {
              return { ...dealType, label: dealType.key };
            }),
            dealType,
          );
        }}
        onUpdate={(values) =>
          setSelectedFilter(
            PortfolioFilterType.DealType,
            values.map((dealType) => {
              return { ...dealType, label: dealType.key };
            }),
            dealType,
            false,
          )
        }
        values={currentView?.selectedFilters.dealTypes}
        allowSearchFilter={false}
        type='am-label'
        displaySubLabel={getSubLabel()}
        onOpen={onOpen}
        onClose={onClose}
        cssClass='exclude-sorting'
        dynamicRender={false}
      />
    </Wrapper>
  );
};

export default PortfolioDealTypeFilter;

export const Wrapper = styled.div``;

const DealTypeFilterBadge = styled(Badge)`
  line-height: 10px;
  border-radius: 2px;
  font-weight: 600;
  font-size: 10px;
  width: 15px;
  height: 15px;
  margin: 2px;
  padding: 4px;
`;
